/**
 * The daily budget the aMER/MER guardrail implies, and the fallback the page
 * uses when no target daily budget is saved. Shared by the page
 * (useRecommendedDailyBudget) and the API's budget_recommend tool so both
 * resolve the same target when Financial Settings lock the daily budget.
 */
const CUBE = 'metrics';

/** Number of trailing days to fetch (wide window to handle data freshness gaps). */
export const DAILY_BUDGET_LOOKBACK_DAYS = 30;

/** Daily revenue + direct spend over the lookback, ending today. */
export function dailyBudgetMetricsQuery(startDate: string, endDate: string) {
  return {
    measures: [
      `${CUBE}.shopify_total_new_customer_revenue`,
      `${CUBE}.shopify_total_customer_revenue`,
      `${CUBE}.shopify_ecomm_new_customer_revenue`,
      `${CUBE}.shopify_ecomm_total_customer_revenue`,
      `${CUBE}.shopify_total_new_customer_orders`,
      `${CUBE}.shopify_ecomm_new_customer_orders`,
      `${CUBE}.total_direct_spend`,
    ],
    timeDimensions: [
      {
        dimension: `${CUBE}.date`,
        dateRange: [startDate, endDate] as [string, string],
        granularity: 'day' as const,
      },
    ],
  };
}

export interface DailyBudgetParams {
  targetMerType: 'amer' | 'mer';
  targetMerValue: number;
  targetMerScope: 'ecomm' | 'total';
  /** Number of complete days to average over (1, 3, or 7). */
  evalPeriodDays: number;
}

export interface DailyBudgetResult {
  recommendedDailyBudget: number | null;
  fallbackDailySpend: number | null;
  /**
   * Average first order value (new-customer revenue / new-customer orders)
   * for `targetMerScope` over every complete day in the fetched window. This
   * is what converts a CAC guardrail to and from its stored aMER
   * (CAC = firstOrderValue / aMER). null when the window has no
   * new-customer orders.
   */
  firstOrderValue: number | null;
  hasData: boolean;
}

const NO_DATA: DailyBudgetResult = {
  recommendedDailyBudget: null,
  fallbackDailySpend: null,
  firstOrderValue: null,
  hasData: false,
};

/** The recommendation from the rows of dailyBudgetMetricsQuery. */
export function dailyBudgetFromMetrics(
  allRows: ReadonlyArray<Record<string, unknown>>,
  { targetMerType, targetMerValue, targetMerScope, evalPeriodDays }: DailyBudgetParams,
): DailyBudgetResult {
  // Use new-customer revenue as a proxy for "this day has data"
  const refMeasure = `${CUBE}.shopify_total_new_customer_revenue`;

  // Keep only days that have actual revenue data, sorted most-recent first
  const daysWithData = allRows
    .filter((row) => Number(row[refMeasure]) > 0)
    .sort((a, b) => {
      const dateA = String(a[`${CUBE}.date.day`] ?? '');
      const dateB = String(b[`${CUBE}.date.day`] ?? '');
      return dateB.localeCompare(dateA);
    });

  if (!daysWithData.length) return NO_DATA;

  // Skip the most recent data day (may be incomplete); every remaining day
  // is complete. The eval window is the next `evalPeriodDays` of them.
  const completeDays = daysWithData.slice(1);
  const rows = completeDays.slice(0, evalPeriodDays);
  if (!rows.length) return NO_DATA;

  const dayCount = rows.length;

  // Average first order value over every complete day (not just the eval
  // window) so a 1-day window does not make the CAC conversion jump around.
  const newCustomerRevenueMeasure =
    targetMerScope === 'ecomm' ? `${CUBE}.shopify_ecomm_new_customer_revenue` : `${CUBE}.shopify_total_new_customer_revenue`;
  const newCustomerOrdersMeasure =
    targetMerScope === 'ecomm' ? `${CUBE}.shopify_ecomm_new_customer_orders` : `${CUBE}.shopify_total_new_customer_orders`;
  let newCustomerRevenue = 0;
  let newCustomerOrders = 0;
  for (const row of completeDays) {
    newCustomerRevenue += Number(row[newCustomerRevenueMeasure]) || 0;
    newCustomerOrders += Number(row[newCustomerOrdersMeasure]) || 0;
  }
  const firstOrderValue = newCustomerOrders > 0 && newCustomerRevenue > 0 ? newCustomerRevenue / newCustomerOrders : null;

  // Select the appropriate revenue measure based on type + scope
  let revenueMeasure: string;
  if (targetMerType === 'amer' && targetMerScope === 'total') {
    revenueMeasure = `${CUBE}.shopify_total_new_customer_revenue`;
  } else if (targetMerType === 'amer' && targetMerScope === 'ecomm') {
    revenueMeasure = `${CUBE}.shopify_ecomm_new_customer_revenue`;
  } else if (targetMerType === 'mer' && targetMerScope === 'total') {
    revenueMeasure = `${CUBE}.shopify_total_customer_revenue`;
  } else {
    revenueMeasure = `${CUBE}.shopify_ecomm_total_customer_revenue`;
  }

  // Sum revenue and spend across selected days
  let totalRevenue = 0;
  let totalDirectSpend = 0;
  for (const row of rows) {
    totalRevenue += Number(row[revenueMeasure]) || 0;
    totalDirectSpend += Number(row[`${CUBE}.total_direct_spend`]) || 0;
  }

  // Calculate fallback daily spend (average total_direct_spend)
  const fallbackDailySpend = totalDirectSpend > 0 ? Math.round((totalDirectSpend / dayCount) * 100) / 100 : null;

  // Calculate recommended daily budget from MER/aMER target
  if (targetMerValue <= 0 || totalRevenue <= 0) {
    return {
      recommendedDailyBudget: null,
      fallbackDailySpend,
      firstOrderValue,
      hasData: true,
    };
  }

  const avgDailyRevenue = totalRevenue / dayCount;
  const rawBudget = avgDailyRevenue / targetMerValue;
  const recommendedDailyBudget = Number.isFinite(rawBudget) && rawBudget > 0 ? Math.round(rawBudget) : null;

  return {
    recommendedDailyBudget,
    fallbackDailySpend,
    firstOrderValue,
    hasData: true,
  };
}
